import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { RtUnitMeasure } from '@bds/models';
import { RtCommodityShipped } from '@bds/railtrac-models';
import { RtUnitMeasureService } from '@bds/services';
import { Observable, firstValueFrom } from 'rxjs';
import { TripCommodityShippedFormService } from './trip-commodity-shipped-form.service';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

@Component({
    selector: 'trip-commodity-shipped',
    templateUrl: './trip-commodity-shipped.component.html',
    styleUrls: ['./trip-commodity-shipped.component.css'],
    providers: [TripCommodityShippedFormService],
})
export class TripCommodityShippedComponent implements OnInit {
    matFloatLabel = 'always';
    matStyle = 'fill';
    dense = true;
    commodityShippedForm: UntypedFormGroup;
    @Input() productOp: 'add' | 'edit' | 'display' = 'display';
    @Input() multiTrip = false;
    @Output() commodityShippedChange = new EventEmitter<RtCommodityShipped>();

    private _commodityShipped: RtCommodityShipped;
    showMoreButton = false;
    showCommodityFact = false;
    moreLessLabel = 'More';
    readonly = false;

    unitMeasureList: RtUnitMeasure[] = [];

    constructor(
        private formService: TripCommodityShippedFormService,
        public unitMeasureService: RtUnitMeasureService,
    ) {
        this.commodityShippedForm = formService.form;
    }

    @Input() set commodityShipped(value: RtCommodityShipped) {
        if (value) {
            this.commodityShippedForm.patchValue(value);
            this._commodityShipped = value;
        }
    }

    get commodityShipped(): RtCommodityShipped {
        return this._commodityShipped;
    }

    ngOnInit(): void {
        if (this.productOp == 'display') {
            this.formService.initReadOnlyForm();
            this.showMoreButton = true;
            this.readonly = true;
        } else {
            // Use valueChanges with debounce and distinctUntilChanged to prevent excessive emissions
            this.commodityShippedForm.valueChanges
                .pipe(
                    debounceTime(300), // Wait 300ms after last change
                    distinctUntilChanged(
                        (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
                    ), // Only emit if value actually changed
                    filter(() => this.commodityShippedForm.valid), // Only emit when form is valid
                )
                .subscribe(() => {
                    const formValue = this.commodityShippedForm.getRawValue();

                    // If multiTrip is true, exclude commodityCompart from the emitted value
                    if (this.multiTrip) {
                        const { commodityCompart, ...valueWithoutCompart } = formValue;
                        this.commodityShippedChange.emit(valueWithoutCompart as RtCommodityShipped);
                    } else {
                        this.commodityShippedChange.emit(formValue);
                    }
                });
        }

        // Disable commodityCompart control if multiTrip is true
        if (this.multiTrip) {
            const commodityCompartControl = this.commodityShippedForm.get('commodityCompart');
            this.commodityShippedForm.get('rtTripOrmId')?.setValue(0);
            this.commodityShippedForm.get('shipDate')?.setValue(Date.now());
            if (commodityCompartControl) {
                commodityCompartControl.disable();
            }
        }

        void this.getAllUnits();
    }

    async getAllUnits(): Promise<void> {
        const results = await firstValueFrom(this.unitMeasureService.getAll());
        this.unitMeasureList =
            results.filter((unit) => unit?.unitType === 'W' || unit?.unitType === 'V') ?? [];
    }

    toggleCommodityFact(): void {
        this.showCommodityFact = !this.showCommodityFact;
        this.moreLessLabel = this.showCommodityFact ? 'Less' : 'More';
    }
}
